import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import SectionCard from './SectionCard';
import SettingRow from './SettingRow';
import StatusPill from './StatusPill';
import settingsStyles from '../settingsStyles';
import {THIRD_PARTY_COMPONENTS_LIST} from '../../../../../constants/appUrls.constants';
import {SEARCH_ENGINE_SUBMISSION_ENABLED} from '../featureFlags';

// Google Search Console ve Bing dizin gonderimi.
// NOT: Servis uclari henuz yok — form durumu yerelde tutuluyor, gonderim islemleri
// onSubmitIndex / onSaveKey prop'lari uzerinden disari aciliyor. Backend hazir olunca
// bu iki prop redux action'larina baglanacak.
export default function SearchEngineCard({
    domain,
    googleStatus,
    bingStatus,
    yandexStatus,
    history = [],
    activeComponents = [],
    onSubmitIndex,
    onSaveKey,
    notReady,
}) {
    const {t} = useTranslation();
    const classes = settingsStyles();

    const [bingKey, setBingKey] = useState('');
    const [yandexKey, setYandexKey] = useState('');
    const [sending, setSending] = useState(null);

    const siteUrl = domain && domain.domain ? `https://${domain.domain}` : '';

    const handleSubmit = (engine) => () => {
        if (typeof onSubmitIndex !== 'function') return notReady && notReady();
        setSending(engine);
        Promise.resolve(onSubmitIndex(engine)).finally(() => setSending(null));
    };

    const handleSaveKey = (engine, key) => () => {
        if (typeof onSaveKey !== 'function') return notReady && notReady();
        onSaveKey({engine, key});
    };

    const googleConnected = googleStatus && googleStatus.connected;
    const bingConnected = bingStatus && bingStatus.connected;
    const yandexConnected = yandexStatus && yandexStatus.connected;

    return (
        <SectionCard
            id="arama"
            icon={SEARCH_ENGINE_SUBMISSION_ENABLED ? 'fas fa-search' : 'fas fa-chart-line'}
            title={SEARCH_ENGINE_SUBMISSION_ENABLED
                ? t('enw-web-settings-seo_title', 'Arama ve Ölçümleme')
                : t('enw-web-settings-tracking_title', 'Ölçümleme')}
            description={SEARCH_ENGINE_SUBMISSION_ENABLED
                ? t('enw-web-settings-seo_desc', 'Dizine gönderim, analiz ve pazarlama etiketleri')
                : t('enw-web-settings-tracking_desc', 'Analiz ve pazarlama etiketleri')}
        >
            {/* Dizine gönderim mekanizması doğrulanana kadar gizli — bkz. featureFlags.js */}
            {SEARCH_ENGINE_SUBMISSION_ENABLED && (
                <>
                <SettingRow
                    icon="fab fa-google"
                    title={t('enw-web-settings-gsc_title', 'Google Search Console')}
                    badge={
                        googleConnected
                            ? <StatusPill tone="ok" label={t('enw-web-settings-connected', 'Bağlı')} />
                            : <StatusPill tone="warn" label={t('enw-web-settings-not_verified', 'Doğrulanmadı')} />
                    }
                    active={!!googleConnected}
                    description={
                        googleConnected && googleStatus.lastSubmit
                            ? t('enw-web-settings-gsc_last', {
                                defaultValue: 'Son gönderim: {{date}} · {{count}} URL',
                                date: googleStatus.lastSubmit,
                                count: googleStatus.lastCount || 0,
                            })
                            : t('enw-web-settings-gsc_desc', 'Doğrulama kodu sitenize eklendikten sonra sayfalarınızı dizine gönderebilirsiniz.')
                    }
                    alwaysOpen
                    control={
                        <Button
                            className={`${classes.btn} ${classes.btnPrimary}`}
                            disabled={sending === 'google'}
                            onClick={handleSubmit('google')}
                            startIcon={<i className="fas fa-cloud-upload-alt" />}
                        >
                            {sending === 'google'
                                ? t('enw-web-settings-sending', 'Gönderiliyor…')
                                : t('enw-web-settings-submit_index', 'Dizine Gönder')}
                        </Button>
                    }
                >
                    <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        label={t('enw-web-settings-gsc_property', 'Mülk (property) adresi')}
                        value={(googleStatus && googleStatus.property) || (domain && domain.domain ? `sc-domain:${domain.domain}` : '')}
                        InputProps={{readOnly: true}}
                    />
                    <p className={classes.hint}>
                        {t('enw-web-settings-gsc_hint', 'Yeni sayfa eklediğinizde veya içerik güncellediğinizde tekrar gönderin. Google günde en fazla 200 URL kabul eder.')}
                        {' '}
                        <Link className={classes.linkish} to={THIRD_PARTY_COMPONENTS_LIST}>
                            {t('enw-web-settings-gsc_verification_link', 'Doğrulama kodunu Google Site Verification bileşeninden girin')}
                        </Link>
                    </p>
                </SettingRow>

                <SettingRow
                    icon="fab fa-microsoft"
                    title={t('enw-web-settings-bing_title', 'Bing Webmaster Tools')}
                    badge={
                        bingConnected
                            ? <StatusPill tone="ok" label={t('enw-web-settings-connected', 'Bağlı')} />
                            : <StatusPill tone="warn" label={t('enw-web-settings-not_verified', 'Doğrulanmadı')} />
                    }
                    active={!!bingConnected}
                    description={t('enw-web-settings-bing_desc', 'Dizine gönderim için önce API anahtarınızı girin.')}
                    alwaysOpen
                    control={
                        bingConnected ? (
                            <Button
                                className={`${classes.btn} ${classes.btnPrimary}`}
                                disabled={sending === 'bing'}
                                onClick={handleSubmit('bing')}
                                startIcon={<i className="fas fa-cloud-upload-alt" />}
                            >
                                {sending === 'bing'
                                    ? t('enw-web-settings-sending', 'Gönderiliyor…')
                                    : t('enw-web-settings-submit_index', 'Dizine Gönder')}
                            </Button>
                        ) : (
                            <Button className={classes.btn} onClick={handleSaveKey('bing', bingKey)}>
                                {t('enw-web-settings-verify', 'Doğrula')}
                            </Button>
                        )
                    }
                >
                    <div className={classes.subGrid}>
                        <TextField
                            variant="outlined"
                            size="small"
                            fullWidth
                            label={t('enw-web-settings-bing_key', 'Bing API anahtarı')}
                            placeholder="örn. 8f2c…a91d"
                            value={bingKey}
                            onChange={(e) => setBingKey(e.target.value)}
                        />
                        <TextField
                            variant="outlined"
                            size="small"
                            fullWidth
                            label={t('enw-web-settings-site_url', 'Site adresi')}
                            value={siteUrl}
                            InputProps={{readOnly: true}}
                        />
                    </div>
                    <p className={classes.hint}>
                        {t('enw-web-settings-bing_hint', 'Anahtarı Bing Webmaster Tools → Ayarlar → API Erişimi bölümünden alabilirsiniz.')}
                    </p>
                </SettingRow>

                <SettingRow
                    icon="fab fa-yandex"
                    title={t('enw-web-settings-yandex_title', 'Yandex Webmaster')}
                    badge={
                        yandexConnected
                            ? <StatusPill tone="ok" label={t('enw-web-settings-connected', 'Bağlı')} />
                            : <StatusPill tone="warn" label={t('enw-web-settings-not_verified', 'Doğrulanmadı')} />
                    }
                    active={!!yandexConnected}
                    description={t('enw-web-settings-yandex_desc', 'Rusça ve BDT pazarına açılan siteler için Yandex dizinine gönderim.')}
                    alwaysOpen
                    control={
                        yandexConnected ? (
                            <Button
                                className={`${classes.btn} ${classes.btnPrimary}`}
                                disabled={sending === 'yandex'}
                                onClick={handleSubmit('yandex')}
                                startIcon={<i className="fas fa-cloud-upload-alt" />}
                            >
                                {sending === 'yandex'
                                    ? t('enw-web-settings-sending', 'Gönderiliyor…')
                                    : t('enw-web-settings-submit_index', 'Dizine Gönder')}
                            </Button>
                        ) : (
                            <Button className={classes.btn} onClick={handleSaveKey('yandex', yandexKey)}>
                                {t('enw-web-settings-verify', 'Doğrula')}
                            </Button>
                        )
                    }
                >
                    <div className={classes.subGrid}>
                        <TextField
                            variant="outlined"
                            size="small"
                            fullWidth
                            label={t('enw-web-settings-yandex_token', 'Yandex OAuth token')}
                            placeholder="örn. y0_AgAAA…"
                            value={yandexKey}
                            onChange={(e) => setYandexKey(e.target.value)}
                        />
                        <TextField
                            variant="outlined"
                            size="small"
                            fullWidth
                            label={t('enw-web-settings-site_url', 'Site adresi')}
                            value={siteUrl}
                            InputProps={{readOnly: true}}
                        />
                    </div>
                    <p className={classes.hint}>
                        {t('enw-web-settings-yandex_hint', 'Token’ı Yandex Webmaster → Ayarlar → Erişim bölümünden oluşturabilirsiniz. Site önce Yandex Webmaster’da doğrulanmış olmalıdır.')}
                    </p>
                </SettingRow>
                </>
            )}

            {/* Analytics / Pixel / GTM / Google yer kimligi hepsi ucuncu parti bilesenler
                ekraninda tanimlaniyor — buradan oraya kisayol veriliyor. */}
            <SettingRow
                icon="fas fa-chart-line"
                title={t('enw-web-settings-tags_title', 'Ölçümleme ve Etiketler')}
                badge={activeComponents.length > 0
                    ? <StatusPill tone="ok" label={t('enw-web-settings-tags_active', {
                        defaultValue: '{{count}} etiket aktif',
                        count: activeComponents.length,
                    })} />
                    : <StatusPill tone="off" label={t('enw-web-settings-tags_none', 'Etiket yok')} />}
                active={activeComponents.length > 0}
                description={t('enw-web-settings-tags_desc', 'Google Analytics, Tag Manager, Meta Pixel, Hotjar ve Google yer kimliği web bileşenleri ekranından tanımlanır.')}
                control={
                    <Button
                        className={classes.btn}
                        component={Link}
                        to={THIRD_PARTY_COMPONENTS_LIST}
                        startIcon={<i className="fas fa-external-link-alt" />}
                    >
                        {t('enw-web-settings-manage_components', 'Bileşenleri Yönet')}
                    </Button>
                }
            >
                {activeComponents.length > 0 && (
                    <div className={classes.tagList}>
                        {activeComponents.map((component) => (
                            <span className={classes.tagChip} key={component.id}>
                                <i className="fas fa-check" />
                                {component.name}
                            </span>
                        ))}
                    </div>
                )}
            </SettingRow>

            {SEARCH_ENGINE_SUBMISSION_ENABLED && history.length > 0 && (
                <div className={classes.tableWrap}>
                    <table className={classes.table}>
                        <thead>
                            <tr>
                                <th className={classes.th}>{t('enw-web-settings-submission', 'Gönderim')}</th>
                                <th className={classes.th}>{t('enw-web-settings-engine', 'Motor')}</th>
                                <th className={classes.th}>URL</th>
                                <th className={classes.th}>{t('enw-web-settings-result', 'Sonuç')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.map((item, key) => (
                                <tr key={key}>
                                    <td className={`${classes.td} ${classes.tdMuted}`}>{item.date}</td>
                                    <td className={classes.td}>{item.engine}</td>
                                    <td className={`${classes.td} ${classes.tdMuted}`}>{item.count}</td>
                                    <td className={classes.td}>
                                        <StatusPill
                                            tone={item.success ? 'ok' : 'warn'}
                                            label={item.message}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </SectionCard>
    );
}
